import { useState, type FormEvent } from "react";
import { createPortal } from "react-dom";

import { ropcLogin } from "./b2cClient";
import { ppB2cConfig } from "./ppToken";

/**
 * Contextual Provider Portal sign-in prompt for the Create-Auth seamless
 * flow (ACT-1247 follow-up). Shown when an SSO/Pathways-authenticated user
 * who is linked-by-email to a Provider Portal account clicks
 * "Create Authorization" but holds no PP bearer token yet.
 *
 * Clones SessionExpiryModal's `ExpiredBody` B2C form shape/styling (no
 * shared Dialog primitive in this repo) with one deliberate difference: the
 * email is LOCKED to the caller-supplied identity. There is no `onChange`
 * for the email field and its value is never read back from the DOM — the
 * `email` prop is the only value ever passed to `ropcLogin`, so there is no
 * code path that lets the user submit a different email than the one they
 * are already signed in as. This also matters for security, not just UX:
 * `ropcLogin` persists this exact email as the stash's `boundIdentityEmail`
 * (see b2cClient.ts), which is what ppToken.ts's cross-identity check
 * compares against — so the binding is only as trustworthy as this lock.
 *
 * Non-dismissable by backdrop click (matches SessionExpiryModal) — the user
 * must explicitly submit or Cancel.
 */
export interface PpLoginModalProps {
  /** Locked identity email — pre-filled, read-only, never editable. */
  email: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function PpLoginModal({ email, onSuccess, onCancel }: PpLoginModalProps) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!password || submitting) return;
    if (!ppB2cConfig) {
      // Defensive only — callers gate on `isPpB2cConfigured` before ever
      // rendering this modal, so this should be unreachable.
      setError("Provider Portal sign-in is not configured in this environment.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // ropcLogin ONLY — this must never call loginWithB2c / touch
      // AuthContext / mutate selectedProvider. The user's primary
      // Entra/Pathways session must survive this untouched; the PP token it
      // writes lands in its own namespaced localStorage slot (b2cClient).
      await ropcLogin(ppB2cConfig, email, password);
      // Best-effort offer-to-save, mirroring LoginPage's maybeSaveCredential.
      // Fire-and-forget — never block the success path on it.
      void maybeSaveCredential(email, password);
      onSuccess();
    } catch (err) {
      // ropcLogin's Error.message is already humanized (b2cClient's
      // AADB2C_FRIENDLY_MESSAGES mapping) — same text SessionExpiryModal
      // surfaces for its own B2C form.
      setError(err instanceof Error ? err.message : "Sign-in failed.");
    } finally {
      setSubmitting(false);
    }
  }

  // Portal to <body> so the fixed overlay is a top-level sibling of #root.
  // Rendering a full-viewport fixed overlay deep in the profile tree can sit
  // under an ancestor that establishes a containing block / compositing layer
  // (transform, filter, contain), which re-rasterizes — and visibly degrades —
  // the background text. Portaling escapes that. (See SwitchPatientModal / #122.)
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pp-login-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm p-4"
    >
      <div className="w-full max-w-md rounded-2xl bg-card text-card-foreground border border-border shadow-2xl p-6 sm:p-7 flex flex-col gap-4">
        <h2 id="pp-login-modal-title" className="text-xl font-bold text-foreground m-0">
          Sign in to Provider Portal
        </h2>
        <p className="text-sm text-muted-foreground m-0">
          Creating an authorization uses your Provider Portal account. Enter your
          Provider Portal password to continue.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3 pt-1">
          <label htmlFor="pp-login-modal-email" className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-foreground/80 uppercase tracking-wide">
              Email
            </span>
            {/* Locked: readOnly + no onChange. The `email` prop, not this
                input's DOM value, is what gets passed to ropcLogin below —
                there is no path from this field back into the submitted
                credentials. Deliberately `readOnly`, NOT `disabled`: most
                password managers (native Chrome/Safari and 1Password/
                Bitwarden-style heuristics alike) exclude `disabled` inputs
                from username-matching entirely, which would suppress the
                paired password offer below. The locked *look* comes from
                the muted/cursor-not-allowed classes, not the attribute.
                id/name/autoComplete are the manager-matching hints (see
                LoginPage.tsx and SessionExpiryModal.tsx for the same
                convention). Note: managers key saved credentials by
                origin — a PP password saved under the Provider Portal
                origin is only offered here by managers capable of
                cross-origin fill/manual-pick; these tokens are still
                required for that to work. */}
            <input
              id="pp-login-modal-email"
              name="username"
              type="email"
              value={email}
              readOnly
              autoComplete="username"
              className="rounded-xl border border-input px-4 py-2.5 text-base text-muted-foreground bg-muted/60 cursor-not-allowed"
            />
          </label>
          <label htmlFor="pp-login-modal-password" className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-foreground/80 uppercase tracking-wide">
              Provider Portal password
            </span>
            <input
              id="pp-login-modal-password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              autoFocus
              className="rounded-xl border border-input px-4 py-2.5 text-base text-foreground bg-background hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all duration-150"
            />
          </label>

          {error && (
            <p role="alert" className="text-sm text-destructive font-medium m-0">
              {error}
            </p>
          )}

          <div className="flex items-center gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !password}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {submitting ? "Signing in..." : "Sign in"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Best-effort offer-to-save via the Credential Management API, same
 * mechanism/rationale as LoginPage.tsx's `maybeSaveCredential` (duplicated
 * rather than shared — it's a few lines and this file already clones
 * SessionExpiryModal's form shape locally). Chrome wires this to its
 * autosave prompt; Safari no-ops; third-party managers don't listen to this
 * API and rely on the id/name/autoComplete hints on the inputs above
 * instead. All failures are silent.
 */
async function maybeSaveCredential(id: string, password: string): Promise<void> {
  try {
    const w = window as Window & {
      PasswordCredential?: new (init: {
        id: string;
        password: string;
        name?: string;
      }) => Credential;
    };
    if (!w.PasswordCredential || !navigator.credentials?.store) return;
    const cred = new w.PasswordCredential({ id, password, name: id });
    await navigator.credentials.store(cred);
  } catch {
    // best-effort
  }
}
